import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import whatsappService from '../services/whatsappService';
import { WhatsAppStatistics, BulkMessageBatch } from '../types/whatsapp';

const WhatsAppAnalyticsPage: React.FC = () => {
  const [statistics, setStatistics] = useState<WhatsAppStatistics | null>(null);
  const [bulkBatches, setBulkBatches] = useState<BulkMessageBatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsResponse, batchesResponse] = await Promise.all([
        whatsappService.getStatistics(dateRange.startDate, dateRange.endDate),
        whatsappService.getBulkBatches()
      ]);
      setStatistics(statsResponse.statistics);
      setBulkBatches(batchesResponse.batches);
    } catch (error: any) {
      showAlert('error', error.message || 'فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type: 'success' | 'error', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleApplyDateRange = () => {
    loadData();
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (loading && !statistics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Prepare chart data
  const statusData = statistics ? [
    { name: 'تم الإرسال', value: statistics.totalSent },
    { name: 'تم التسليم', value: statistics.totalDelivered },
    { name: 'تمت القراءة', value: statistics.totalRead },
    { name: 'فشل', value: statistics.totalFailed }
  ] : [];

  const providerData = statistics ? Object.entries(statistics.byProvider).map(([name, value]) => ({
    name,
    value
  })) : [];

  const messageTypeData = statistics ? Object.entries(statistics.byMessageType).map(([name, value]) => ({
    name: name === 'template' ? 'قالب' : name === 'text' ? 'نص' : 'وسائط',
    value
  })) : [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          إحصائيات واتساب
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
        >
          تحديث
        </Button>
      </Box>

      {/* Alert */}
      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Date Range Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                label="من تاريخ"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                label="إلى تاريخ"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyDateRange}
                disabled={loading}
              >
                تطبيق
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {statistics && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SendIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h4">{statistics.totalSent}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        إجمالي المرسل
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h4">{statistics.totalDelivered}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        تم التسليم
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={parseFloat(statistics.deliveryRate)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    معدل التسليم: {statistics.deliveryRate}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <VisibilityIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h4">{statistics.totalRead}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        تمت القراءة
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={parseFloat(statistics.readRate)}
                    color="info"
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    معدل القراءة: {statistics.readRate}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ErrorIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h4">{statistics.totalFailed}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        فشل
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Status Distribution */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    توزيع حالات الرسائل
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Provider Distribution */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    توزيع المزودين
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={providerData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Message Type Distribution */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    أنواع الرسائل
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={messageTypeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#82ca9d"
                        dataKey="value"
                      >
                        {messageTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Bulk Batches */}
          {bulkBatches.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  الحملات الجماعية الأخيرة
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>اسم الحملة</TableCell>
                        <TableCell>النوع</TableCell>
                        <TableCell>إجمالي المستلمين</TableCell>
                        <TableCell>النجاح</TableCell>
                        <TableCell>الفشل</TableCell>
                        <TableCell>الحالة</TableCell>
                        <TableCell>التقدم</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkBatches.slice(0, 10).map((batch) => (
                        <TableRow key={batch._id}>
                          <TableCell>{batch.name}</TableCell>
                          <TableCell>{batch.campaignType}</TableCell>
                          <TableCell>{batch.totalRecipients}</TableCell>
                          <TableCell>
                            <Typography color="success.main">
                              {batch.successCount}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography color="error.main">
                              {batch.failedCount}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{
                                color:
                                  batch.status === 'completed'
                                    ? 'success.main'
                                    : batch.status === 'failed'
                                    ? 'error.main'
                                    : 'warning.main'
                              }}
                            >
                              {batch.status}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={batch.progressPercentage}
                                sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                              />
                              <Typography variant="caption">
                                {Math.round(batch.progressPercentage)}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default WhatsAppAnalyticsPage;